/**
 * Subscription service for managing premium subscriptions.
 * 
 * Provides methods for:
 * - Getting subscription status
 * - Creating checkout sessions for upgrades
 * - Creating billing portal sessions for management
 */
angular.module('dentalLinksServices')

.factory('Subscription', ['$resource', 'API_ENDPOINT', 'NO_CACHE_HEADERS',
    function ($resource, API_ENDPOINT, NO_CACHE_HEADERS) {
        return $resource(API_ENDPOINT + '/subscriptions', {}, {
            // GET /subscriptions/status - Get current subscription status
            getStatus: {
                method: 'GET',
                url: API_ENDPOINT + '/subscriptions/status',
                headers: NO_CACHE_HEADERS
            },
            // GET /insights - What DCL delivered to this practice (DCL-592)
            getInsights: {
                method: 'GET',
                url: API_ENDPOINT + '/insights',
                headers: NO_CACHE_HEADERS
            },
            // GET /subscriptions/prices - Get available subscription prices
            getPrices: {
                method: 'GET',
                url: API_ENDPOINT + '/subscriptions/prices'
            },
            // POST /subscriptions/checkout - Create Stripe Checkout session
            createCheckout: {
                method: 'POST',
                url: API_ENDPOINT + '/subscriptions/checkout'
            },
            // POST /subscriptions/billing_portal - Create Stripe Billing Portal session
            createBillingPortal: {
                method: 'POST',
                url: API_ENDPOINT + '/subscriptions/billing_portal'
            },
            // POST /subscriptions/change_interval - Change billing interval
            changeInterval: {
                method: 'POST',
                url: API_ENDPOINT + '/subscriptions/change_interval'
            }
        });
    }])

/**
 * Subscription helper service with utility methods.
 */
.factory('SubscriptionHelper', ['$window', 'SITE_URL', 'FREE_TRIAL_PERIOD',
    function ($window, SITE_URL, FREE_TRIAL_PERIOD) {
        return {
            /**
             * Redirects to Stripe Checkout for subscription upgrade.
             * @param {Object} Subscription - The Subscription resource
             * @param {string} interval - 'month' or 'year'
             * @param {function} onError - Error callback
             */
            redirectToCheckout: function (Subscription, interval, onError) {
                var successUrl = SITE_URL + '#!/admin/practice?subscription=success';
                var cancelUrl = SITE_URL + '#!/admin/practice?subscription=cancelled';

                Subscription.createCheckout({
                    interval: interval,
                    success_url: successUrl,
                    cancel_url: cancelUrl
                }, function (response) {
                    // Redirect to Stripe Checkout
                    $window.location.href = response.checkout_url;
                }, function (error) {
                    if (onError) {
                        onError(error);
                    }
                });
            },

            /**
             * Redirects to Stripe Billing Portal for subscription management.
             * @param {Object} Subscription - The Subscription resource
             * @param {function} onError - Error callback
             */
            redirectToBillingPortal: function (Subscription, onError) {
                var returnUrl = SITE_URL + '#!/admin/practice';

                Subscription.createBillingPortal({
                    return_url: returnUrl
                }, function (response) {
                    // Redirect to Stripe Billing Portal
                    $window.location.href = response.portal_url;
                }, function (error) {
                    if (onError) {
                        onError(error);
                    }
                });
            },

            /**
             * Formats a price in cents to a display string.
             * @param {number} cents - Price in cents
             * @returns {string} Formatted price rounded to whole dollars (e.g., "$99")
             */
            formatPrice: function (cents) {
                if (!cents) return '$0';
                return '$' + (cents / 100).toFixed(0);
            },

            /**
             * Formats a billing interval for display.
             * @param {string} interval - 'month' or 'year'
             * @returns {string} Display text (e.g., "Monthly", "Annual")
             */
            formatInterval: function (interval) {
                switch (interval) {
                    case 'month':
                        return 'Monthly';
                    case 'year':
                        return 'Annual';
                    default:
                        return interval;
                }
            },

            /**
             * Gets a human-readable status description.
             * @param {Object} subscriptionData - Subscription status from API
             * @returns {string} Status description
             */
            getStatusDescription: function (subscriptionData) {
                if (!subscriptionData.has_subscription) {
                    return 'Basic (Free)';
                }
                // DCL-508: a complimentary (comped) trial has no Stripe record, so
                // none of the stripe_status branches can ever describe it. It is
                // never "cancelling" either — nothing renews it; it just ends.
                if (subscriptionData.subscription.complimentary) {
                    return subscriptionData.premium_access ? 'Premium (Complimentary Trial)' : 'Trial Ended';
                }
                if (subscriptionData.subscription.pending_downgrade) {
                    return 'Premium (Cancelling)';
                }
                if (subscriptionData.subscription.stripe_status === 'trialing') {
                    return 'Premium (Trial)';
                }
                if (subscriptionData.subscription.stripe_status === 'past_due') {
                    return 'Premium (Payment Due)';
                }
                if (subscriptionData.premium_access) {
                    return 'Premium';
                }
                return 'Expired';
            },

            /**
             * Label for the main upgrade button (DCL-561): practices that never
             * redeemed the one-time free trial are invited into it; everyone
             * else keeps the plain upgrade wording. trial_available comes from
             * the backend's Redemption ledger — never decided client-side.
             * @param {Object} subscriptionData - Subscription status from API
             * @returns {string} Button label
             */
            getUpgradeButtonText: function (subscriptionData) {
                if (!subscriptionData) {
                    return '';
                }
                if (subscriptionData.premium_access) {
                    return 'Keep Premium';
                }
                if (subscriptionData.trial_available) {
                    return 'Try Premium Free for ' + FREE_TRIAL_PERIOD + ' Days';
                }
                return 'Upgrade to Premium';
            }
        };
    }]);
